import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Ear, Flag, FlagOff, GitBranch, Mic, MicOff, PhoneOff, Undo2, X } from "lucide-react";

import { useHasPermission } from "../../../context/AuthContext";
import { useCallDetail } from "../use-call-detail";
import type { Alert, SupervisedCall } from "../types";
import { useAudioSession, type SessionOutcome } from "../audio/audio-provider";
import { HoldButton } from "../controls/hold-button";
import { alertLabel, formatAge } from "../alert-badge";
import { SpeechCutTotals } from "./speech-cut";
import { TranscriptView } from "./transcript-view";
import type { TranscriptLanguageMode } from "../lib/transcript-language";
import { markAlertFalsePositive, sendGuidance, flagCall, unflagCall } from "../supervision-api";
import { FlagControls } from "../flag-controls";
import { getManualFlag, removeManualFlag, saveManualFlag } from "../lib/flags-store";
import { cn } from "../../../lib/utils";
import { toast } from "sonner";

export function CallDrawer({
  call,
  onClose,
  languageMode = "both",
  onLanguageModeChange,
}: {
  call: SupervisedCall | null;
  onClose: () => void;
  languageMode?: TranscriptLanguageMode;
  onLanguageModeChange?: (mode: TranscriptLanguageMode) => void;
}) {
  const { detail, messages, alerts, error, applyAlert, reload } = useCallDetail(
    call?.call_id ?? null,
    call,
  );
  const session = useAudioSession();
  const canListen = useHasPermission("supervision.listen");
  const canTakeOver = useHasPermission("supervision.takeover");
  const audioCallId = session.callId ?? session.pendingCallId;
  const stopAudio = session.stop;

  const [mode, setMode] = useState<"steer" | "speak" | null>(null);
  const [guidanceInput, setGuidanceInput] = useState("");
  const [isListeningSpeech, setIsListeningSpeech] = useState(false);
  const recognitionRef = useRef<any>(null);

  const stopSpeechRecognition = useCallback(() => {
    if (recognitionRef.current) {
      try {
        if (typeof recognitionRef.current.abort === "function") {
          recognitionRef.current.abort();
        } else if (typeof recognitionRef.current.stop === "function") {
          recognitionRef.current.stop();
        }
      } catch {}
      recognitionRef.current = null;
    }
    setIsListeningSpeech(false);
  }, []);

  const startSpeechRecognition = useCallback(() => {
    stopSpeechRecognition();
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      toast.error("Speech recognition is not supported in this browser. You can type instructions directly.");
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = navigator.language || "en-US";

      const prefix = guidanceInput ? guidanceInput.trim() + " " : "";

      recognition.onstart = () => {
        setIsListeningSpeech(true);
      };

      recognition.onresult = (event: any) => {
        let transcript = "";
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        if (transcript) {
          setGuidanceInput((prefix + transcript).trim());
        }
      };

      recognition.onerror = (event: any) => {
        console.warn("Speech recognition error:", event.error);
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          toast.error("Microphone access was denied. Please allow microphone access.");
        }
        setIsListeningSpeech(false);
      };

      recognition.onend = () => {
        setIsListeningSpeech(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error("Speech recognition start failed:", err);
      setIsListeningSpeech(false);
    }
  }, [guidanceInput, stopSpeechRecognition]);

  useEffect(() => {
    return () => {
      stopSpeechRecognition();
    };
  }, [stopSpeechRecognition]);

  const handleOpenSpeak = () => {
    if (mode === "speak") {
      if (isListeningSpeech) {
        stopSpeechRecognition();
      } else {
        startSpeechRecognition();
      }
      return;
    }
    setMode("speak");
    setGuidanceInput("");
    setTimeout(() => {
      startSpeechRecognition();
    }, 50);
  };

  const handleOpenSteer = () => {
    stopSpeechRecognition();
    setMode("steer");
  };

  const handleCancelGuidance = () => {
    stopSpeechRecognition();
    setGuidanceInput("");
    setMode(null);
  };

  const lifecycle = detail?.lifecycle === "ended_complete" ? "ended_complete"
    : call?.lifecycle?.startsWith("ended") ? call.lifecycle : detail?.lifecycle ?? call?.lifecycle ?? "live";
  const live = lifecycle === "live";
  const ended = lifecycle.startsWith("ended");

  useEffect(() => {
    if (ended && audioCallId === call?.call_id) void stopAudio();
  }, [ended, audioCallId, stopAudio, call?.call_id]);

  useEffect(() => {
    const refresh = () => void reload();
    window.addEventListener("supervision-flags-changed", refresh);
    return () => window.removeEventListener("supervision-flags-changed", refresh);
  }, [reload]);

  // Escape closes
  useEffect(() => {
    if (!call) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [call, onClose]);

  if (!call) return null;

  const name = detail?.customer_name || detail?.contact_name || call.contact_name || "Customer";
  const phone = detail?.customer_phone || call.customer_phone || call.contact_id || call.call_id;
  const campaignName = call.campaign || call.agent_name || detail?.campaign || "General";
  const rawTier = (detail?.risk ?? call.risk)?.level ?? (call.severity === "critical" ? "red" : call.severity === "warn" ? "amber" : "green");
  const riskTier = rawTier === "red" || rawTier === "amber" || rawTier === "green" ? rawTier : "green";
  const riskScore = (detail?.risk ?? call.risk)?.score;

  const isListening = session.callId === call.call_id && session.tier === "listen";
  const isHeld = session.callId === call.call_id && session.tier === "takeover";
  // Listening or taking over elsewhere drops this hold's room and heartbeat while the AI
  // stays held, so both wait until the supervisor hands back.
  const holdingAnyCall = session.callId !== null && session.tier === "takeover";
  const takingOver = session.busy === "takeover" && session.pendingCallId === call.call_id;

  // Session actions never throw; toast what actually happened.
  const report = (outcome: SessionOutcome, success: string) => {
    if (outcome.status === "done") toast.success(success);
    else if (outcome.status === "failed") toast.error(outcome.error);
  };

  const handleListen = async () => {
    if (isListening) {
      await session.stop();
      toast.info("Stopped listening");
    } else {
      report(await session.listen(call.call_id), "Listening to call");
    }
  };

  const handleTakeover = async () => {
    report(await session.takeover(call.call_id), "You are on the call — the AI is on hold");
  };

  const handleHandBack = async () => {
    report(await session.handBack(), "Call handed back to the AI");
  };

  const handleEndCall = async () => {
    report(await session.endCall(call.call_id), "Call ended");
  };

  const handleSendGuidance = async () => {
    if (!guidanceInput.trim()) return;
    stopSpeechRecognition();
    try {
      await sendGuidance(call.call_id, {
        text: guidanceInput.trim(),
        urgency: "next_turn",
        source: "typed",
      });
      toast.success("Guidance sent to AI agent");
      setGuidanceInput("");
      setMode(null);
    } catch {
      toast.error("Failed to deliver guidance");
    }
  };

  const handleDismissFalsePos = async (targetAlert?: Alert) => {
    const alertToDismiss = targetAlert || alerts.find((a) => a.status === "open" || a.status === "acknowledged");
    if (!alertToDismiss) {
      toast.info("No active alerts to dismiss");
      return;
    }
    try {
      const res = await markAlertFalsePositive(alertToDismiss.alert_id);
      applyAlert(res.alert);
      toast.success(`Dismissed ${alertToDismiss.detail || alertToDismiss.type} as false positive`);
    } catch {
      toast.error("Failed to dismiss alert");
    }
  };

  const currentFlag = detail?.flag ?? getManualFlag(call.call_id);

  const handleToggleFlag = async () => {
    try {
      if (currentFlag) {
        await unflagCall(call.call_id);
        removeManualFlag(call.call_id);
        toast.info("Call unflagged");
      } else {
        const meta: Partial<SupervisedCall> = {
          call_id: call.call_id,
          contact_name: name,
          customer_phone: phone,
          agent_name: campaignName,
          campaign: campaignName,
          lifecycle: live ? "live" : "ended_complete",
        };
        await flagCall(call.call_id, "");
        saveManualFlag(call.call_id, "", meta);
        toast.success("Call flagged for review");
      }
      reload();
      window.dispatchEvent(new Event("supervision-flags-changed"));
    } catch (err) {
      toast.error((err as Error).message || "Could not update call flag");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="absolute inset-0 z-40 flex justify-end bg-black/25 backdrop-blur-[0.5px]"
      onClick={onClose}
    >
      <motion.aside
        role="region"
        aria-label={`${live ? "Live call" : "Call review"} with ${name}`}
        data-testid="supervision-call-drawer"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 320 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[min(760px,65vw)] h-full flex flex-col overflow-hidden bg-card border-l border-border shadow-2xl select-none"
      >
        {/* 2-Pane Header */}
      <div className="h-10 px-3 border-b border-border flex items-center justify-between bg-muted/40 shrink-0 select-none">
        <div className="flex items-center gap-2.5 min-w-0 font-mono text-xs">
          <span
            className={`font-bold ${
              riskTier === "red"
                ? "text-[#ef4444]"
                : riskTier === "amber"
                ? "text-[#f59e0b]"
                : "text-muted-foreground"
            }`}
          >
            {riskTier.toUpperCase()}{riskScore != null ? ` ${riskScore}` : ""}
          </span>
          <span className="font-semibold text-foreground">{phone}</span>
          <span className="text-muted-foreground truncate">
            {name} · {campaignName}
          </span>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-border bg-muted/30 text-foreground uppercase">
            {live ? "LIVE" : ended ? "ENDED" : "UNAVAILABLE"}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <FlagControls
            callId={call.call_id}
            flag={currentFlag}
            onChanged={() => void reload()}
          />
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition"
            aria-label="Close drawer"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* 2-PANE BODY */}
      <div className="flex-1 min-h-0 flex divide-x divide-border overflow-hidden">
        {/* PANE 1: LIVE TRANSCRIPT */}
        <div className="flex-1 min-w-0 flex flex-col h-full bg-card">
          <div className="px-3 py-1.5 border-b border-border/40 flex items-center justify-between gap-2">
            <span className="text-[9px] font-mono font-bold tracking-widest text-muted-foreground uppercase shrink-0">
              LIVE TRANSCRIPT
            </span>
            {/* Call-level rollup, so a repeated cut-off is visible at a glance
                rather than only turn by turn. Absent on a call with nothing
                measured. */}
            <SpeechCutTotals messages={messages} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto column-scroll">
            <TranscriptView
              messages={messages}
              alerts={alerts}
              live={live}
              languageMode={languageMode}
              onLanguageModeChange={onLanguageModeChange}
            />
          </div>
        </div>

        {/* PANE 2: CUSTOMER & CONTEXT RAIL */}
        <div className="w-[260px] shrink-0 flex flex-col h-full bg-muted/15 overflow-y-auto hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden p-3 text-xs font-mono select-none divide-y divide-border/40">
          {/* Section: Customer Metadata */}
          <div className="pb-3">
            <div className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase mb-2">
              CUSTOMER
            </div>
            <div className="space-y-1.5 text-[11px]">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">ID</span>
                <span className="font-semibold text-foreground truncate max-w-[140px]">{phone}</span>
              </div>
            </div>
          </div>

          {/* Section: Risk Events */}
          <div className="py-3">
            <div className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase mb-1.5 flex items-center justify-between">
              <span>RISK EVENTS</span>
              {alerts.length > 0 && <span className="text-risk-amber font-semibold">({alerts.length})</span>}
            </div>
            {alerts.length === 0 ? (
              <span className="text-muted-foreground text-[11px] italic">none fired</span>
            ) : (
              <div className="space-y-1.5">
                {alerts.map((a) => (
                  <div
                    key={a.alert_id}
                    className={`p-1.5 rounded border text-[10px] leading-tight ${
                      a.status === "false_positive"
                        ? "line-through opacity-50 border-border bg-background"
                        : a.severity === "critical"
                        ? "border-red-300/80 bg-red-50/80 dark:bg-red-950/20 text-[#ef4444]"
                        : "border-amber-300/80 bg-amber-50/80 dark:bg-amber-950/20 text-[#f59e0b]"
                    }`}
                  >
                    <div className="font-semibold flex items-center justify-between">
                      <span className="truncate">{a.detail || alertLabel(a)}</span>
                      {a.status !== "false_positive" && (
                        <button
                          onClick={() => void handleDismissFalsePos(a)}
                          className="hover:underline flex items-center gap-0.5 text-[8px] text-muted-foreground hover:text-foreground shrink-0"
                          title="Mark as false positive"
                        >
                          <FlagOff size={9} /> False+
                        </button>
                      )}
                    </div>
                    <div className="text-muted-foreground text-[9px] mt-0.5">
                      {a.age_seconds != null ? `${formatAge(a.age_seconds)} ago` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section: Interventions */}
          <div className="pt-3">
            <div className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase mb-1.5">
              INTERVENTIONS
            </div>
            <div className="space-y-1 text-[10px]">
              {isHeld ? (
                <div className="text-primary font-semibold">Supervisor Takeover active</div>
              ) : isListening ? (
                <div className="text-emerald-500 font-semibold">Supervisor Listening active</div>
              ) : (
                <span className="text-muted-foreground text-[11px] italic">none yet</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Steer/Speak guidance input */}
      {mode && (
        <div className="p-2 border-t border-border bg-muted/40 flex flex-col gap-2 shrink-0">
          <div className="flex items-center gap-2">
            {mode === "speak" && (
              <button
                type="button"
                onClick={() => (isListeningSpeech ? stopSpeechRecognition() : startSpeechRecognition())}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono font-semibold rounded border transition shrink-0",
                  isListeningSpeech
                    ? "border-red-500 bg-red-500/10 text-red-600 dark:text-red-400 animate-pulse"
                    : "border-border bg-background hover:bg-muted text-muted-foreground"
                )}
                title={isListeningSpeech ? "Microphone active: click to pause listening" : "Microphone paused: click to resume speaking"}
              >
                {isListeningSpeech ? (
                  <>
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-ping" />
                    <Mic size={13} className="text-red-500" />
                    <span>Listening…</span>
                  </>
                ) : (
                  <>
                    <MicOff size={13} />
                    <span>Mic Off</span>
                  </>
                )}
              </button>
            )}

            <input
              autoFocus
              type="text"
              value={guidanceInput}
              onChange={(e) => setGuidanceInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendGuidance()}
              placeholder={
                mode === "steer"
                  ? "Guide AI agent on next turn..."
                  : isListeningSpeech
                    ? "Listening... Speak your guidance now"
                    : "Spoken guidance (or type instruction)..."
              }
              className="flex-1 bg-background border border-border px-2.5 py-1 text-xs text-foreground placeholder:text-muted-foreground rounded outline-none focus:border-primary font-mono"
            />
            <button
              type="button"
              onClick={handleSendGuidance}
              disabled={!guidanceInput.trim()}
              className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded hover:opacity-90 font-mono disabled:opacity-40"
            >
              Send
            </button>
            <button
              type="button"
              onClick={handleCancelGuidance}
              className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground font-mono"
            >
              Cancel
            </button>
          </div>
          {mode === "speak" && (
            <div className="flex items-center justify-between px-1 text-[10px] text-muted-foreground font-mono">
              <span>{isListeningSpeech ? "Speak into your microphone — speech is transcribed automatically" : "Speech paused. Edit text or toggle mic to continue."}</span>
              <span className="italic">Delivered to AI agent</span>
            </div>
          )}
        </div>
      )}

      {session.error && (
        <div
          role="alert"
          data-testid="supervision-session-error"
          className="px-3 py-1.5 border-t border-border bg-red-50 dark:bg-red-950/30 text-[11px] text-destructive flex items-center gap-2 shrink-0"
        >
          <span className="flex-1">{session.error}</span>
          <button type="button" onClick={session.clearError} aria-label="Dismiss error" className="p-0.5 hover:text-foreground">
            <X size={12} />
          </button>
        </div>
      )}

      {/* BOTTOM ACTION TOOLBAR */}
      {live && (
        <div className="p-2 border-t border-border bg-card flex items-center gap-1.5 shrink-0 text-xs font-mono select-none">
          {canListen && (
            <button
              type="button"
              onClick={handleListen}
              disabled={session.busy !== null || holdingAnyCall}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-2 border border-border bg-background hover:bg-muted text-foreground rounded font-semibold transition disabled:opacity-40"
              title={holdingAnyCall ? "Hand back the call you are on first" : isListening ? "Stop listening" : "Listen in to this call"}
            >
              <Ear size={13} className={isListening ? "text-emerald-500" : "text-muted-foreground"} />
              <span>{isListening ? "LISTENING" : "LISTEN"}</span>
            </button>
          )}

          <button
            type="button"
            onClick={handleOpenSteer}
            className={cn(
              "flex-1 flex items-center justify-center gap-1 px-2 py-2 border border-border rounded font-semibold transition",
              mode === "steer"
                ? "bg-primary/10 border-primary/40 text-primary font-bold"
                : "bg-background hover:bg-muted text-foreground"
            )}
            title="Guide the AI agent"
          >
            <GitBranch size={13} className={mode === "steer" ? "text-primary" : "text-muted-foreground"} />
            <span>STEER</span>
          </button>

          <button
            type="button"
            onClick={handleOpenSpeak}
            className={cn(
              "flex-1 flex items-center justify-center gap-1 px-2 py-2 border border-border rounded font-semibold transition",
              mode === "speak"
                ? "bg-red-500/10 border-red-500/40 text-red-600 dark:text-red-400 font-bold"
                : "bg-background hover:bg-muted text-foreground"
            )}
            title="Speak into the call"
          >
            <Mic size={13} className={mode === "speak" ? "text-red-500" : "text-muted-foreground"} />
            <span>SPEAK</span>
          </button>

          {canTakeOver && (isHeld ? (
            <button
              type="button"
              data-testid="handback-button"
              onClick={handleHandBack}
              disabled={session.busy !== null}
              className="px-4 py-2 text-[11px] font-mono font-bold bg-primary text-primary-foreground hover:opacity-90 rounded flex items-center justify-center gap-1.5 shrink-0 disabled:opacity-40"
              title="Give the call back to the AI"
            >
              <Undo2 size={13} />
              <span className="tracking-wider">{session.busy === "handback" ? "HANDING BACK…" : "HAND BACK"}</span>
            </button>
          ) : (
            <HoldButton
              testId="takeover-button"
              onHold={() => void handleTakeover()}
              disabled={session.busy !== null || holdingAnyCall}
              title={holdingAnyCall ? "Hand back the call you are on first" : "Hold to speak to the caller yourself. The AI says a hand-off line, then goes quiet."}
              className="px-4 py-2 text-[11px] font-mono font-bold bg-[#ea580c] hover:bg-[#c2410c] text-white rounded justify-center shrink-0"
            >
              <span className="tracking-wider">{takingOver ? "TAKING OVER…" : "HOLD TO TAKE OVER"}</span>
            </HoldButton>
          ))}

          {canTakeOver && (
            <button
              type="button"
              onClick={handleEndCall}
              disabled={session.busy !== null}
              className="px-3 py-2 border border-border bg-background hover:bg-red-50 dark:hover:bg-red-950/30 text-foreground hover:text-destructive rounded font-semibold transition flex items-center gap-1 disabled:opacity-40"
              title="End this call immediately"
            >
              <PhoneOff size={13} className="text-muted-foreground" />
              <span>END</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => void handleDismissFalsePos()}
            className="px-3 py-2 border border-border bg-background hover:bg-muted text-foreground rounded font-semibold transition flex items-center gap-1"
            title="Mark top alert as false positive"
          >
            <FlagOff size={13} className="text-muted-foreground" />
            <span>FALSE POS</span>
          </button>

          <button
            type="button"
            onClick={handleToggleFlag}
            data-testid="flag-call-button"
            className={cn(
              "px-3 py-2 border rounded font-semibold transition flex items-center gap-1 shrink-0",
              currentFlag
                ? "border-amber-500/50 bg-amber-500/10 text-amber-500 dark:text-amber-400"
                : "border-border bg-background hover:bg-muted text-foreground"
            )}
            title={currentFlag ? "Unflag call" : "Flag call manually for supervisor review"}
          >
            <Flag size={13} className={currentFlag ? "fill-current text-amber-500" : "text-muted-foreground"} />
            <span>{currentFlag ? "FLAGGED" : "FLAG"}</span>
          </button>
        </div>
      )}
    </motion.aside>
  </motion.div>
  );
}
